import AntIcon from "@/components/icons/AntIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { selectStandardProps } from "@/utils/componentUtils";
import { stripAccents } from "@/utils/utils";
import { Button, Divider, Empty, Select, Spin } from "antd";
import { DefaultOptionType } from "antd/es/select";
import debounce from "lodash/debounce";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGetAgentEnumerations, useSearchAgentEnumerations } from "../../../../common/api/queries";
import type { UUID } from "../../../../typings/global";
import { AgentType } from "../../../agent/enums";
import { formatAgentName } from "../../../agent/utils";
import { AgentEnumeration } from "../../types";
import styles from "./AgentSelect.module.scss";

const renderAgentOption = (agent: AgentEnumeration): DefaultOptionType => {
  const agentName = formatAgentName(agent);
  const representatives = agent.representatives?.map((r) => formatAgentName(r, false)).join(", ");

  return {
    key: agent.id,
    label: (
      <>
        <div>{agentName}</div>
        {representatives && <Ellipsis className={styles.selectOptionSecondaryRow}>{representatives}</Ellipsis>}
      </>
    ),
    title: agentName,
    value: agent.id,
    props: { representatives },
  };
};

interface AgentOptionsProps {
  defaultOption?: AgentEnumeration;
  allowedIds?: UUID[];
  excludedIds?: UUID[];
  subordinateTreePath?: string;
  superiorTreePath?: string;
  agentTypes?: AgentType[];
  onlyActive?: boolean;
  onlyCanBeGainer?: boolean;
  forceRender?: boolean;
}

interface Props<T extends boolean> {
  optionsProps?: AgentOptionsProps;
  parentFieldName?: string;
  isMultiple?: T;
  allowClear?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  onChange?: (data: T extends true ? UUID[] : UUID) => void;
  className?: string;
  value?: T extends true ? UUID[] : UUID;
  onExtended?: () => void;
}

export const AgentSelect = <T extends boolean = false>({
  optionsProps,
  isMultiple,
  allowClear,
  disabled,
  style,
  onChange,
  className,
  value,
  onExtended,
}: Props<T>) => {
  const { t } = useTranslation();

  const [searchKeyword, setSearchKeyword] = useState("");
  const [debounceKeyword, setDebounceKeyword] = useState("");
  const [isSelectBoxOpen, setIsSelectBoxOpen] = useState(false);

  const { data: currentDataAgents, isFetching: isFetchingCurrentAgents } = useGetAgentEnumerations(
    Array.isArray(value) ? value : value ? [value] : [],
    true,
  );

  const { data: allowedAgents } = useGetAgentEnumerations((optionsProps?.forceRender && optionsProps.allowedIds) || []);

  const { data: searchAgentData, isFetching } = useSearchAgentEnumerations({
    keyword: debounceKeyword,
    subordinateTreePath: optionsProps?.subordinateTreePath,
    allowedIds: optionsProps?.allowedIds,
    excludedIds: optionsProps?.excludedIds,
    types: optionsProps?.agentTypes,
    onlyActive: optionsProps?.onlyActive,
    onlyCanBeGainer: optionsProps?.onlyCanBeGainer,
    superiorTreePath: optionsProps?.superiorTreePath,
  });

  const defaultOption = optionsProps?.defaultOption;

  const selectedOptions = useMemo(
    () => currentDataAgents?.map((agent) => renderAgentOption(agent)),
    [currentDataAgents],
  );

  const renderedAgentOptions: DefaultOptionType[] = useMemo(() => {
    const agentOptions = allowedAgents?.length ? [...allowedAgents] : [...(searchAgentData?.data ?? [])];

    if (defaultOption && !agentOptions.some((agent) => agent.id === defaultOption.id)) {
      agentOptions.unshift(defaultOption);
    }

    const options: DefaultOptionType[] = agentOptions.map((agent) => renderAgentOption(agent));

    if (!selectedOptions) {
      return options;
    }

    const extraOptions = selectedOptions.filter((opt) => !options.some((o) => o.key === opt.key));

    return [...options, ...extraOptions];
  }, [allowedAgents, searchAgentData, defaultOption, selectedOptions]);

  const handleOptionSearchDebounced = useMemo(
    () =>
      debounce((keyword: string) => {
        setDebounceKeyword(keyword);
      }, 500),
    [],
  );

  const handleOptionSearch = useCallback(
    (input: string) => {
      handleOptionSearchDebounced(input);
      setSearchKeyword(input);
    },
    [handleOptionSearchDebounced],
  );

  const handleOptionFilter = (input: string, option?: Record<string, any>): boolean => {
    if (!option) {
      return false;
    }

    return (
      stripAccents((option["title"] + (option["props"]["representatives"] || "")).toLowerCase()).indexOf(
        stripAccents(input.toLowerCase()),
      ) !== -1
    );
  };

  const onChangeSelect = (selectedValue: T extends true ? UUID[] : UUID) => {
    onChange?.(selectedValue);

    if (allowedAgents?.length) {
      return;
    }

    setSearchKeyword("");
    setDebounceKeyword("");
  };

  return (
    <Select
      {...selectStandardProps}
      showSearch={{
        filterOption: allowedAgents?.length ? handleOptionFilter : () => true,
        onSearch: allowedAgents?.length ? undefined : handleOptionSearch,
        searchValue: allowedAgents?.length ? undefined : searchKeyword,
      }}
      className={className}
      style={style}
      allowClear={allowClear}
      disabled={disabled}
      maxTagCount="responsive"
      mode={isMultiple ? "multiple" : undefined}
      open={isSelectBoxOpen}
      onOpenChange={setIsSelectBoxOpen}
      onChange={onChangeSelect}
      optionLabelProp="title"
      options={renderedAgentOptions}
      value={value}
      loading={isFetching || isFetchingCurrentAgents}
      labelRender={(item) => item.label ?? ""}
      placeholder={t("components.agentSelect.placeholder")}
      notFoundContent={
        <>
          {debounceKeyword.length > 1 && renderedAgentOptions.length === 0 && !isFetching ? (
            <Empty description={t("components.agentSelect.noData")} />
          ) : (
            <Spin spinning={isFetching}>
              <Empty description={t("components.agentSelect.searchHint")} />
            </Spin>
          )}
        </>
      }
      popupRender={(menu) => (
        <>
          {menu}

          {renderedAgentOptions.length ? (
            <div className="margin-bottom-tiny center-align sub-header-info" style={{ margin: "10px 6px 8px" }}>
              <AntIcon type="search" /> {t("components.agentSelect.searchHint")}
            </div>
          ) : undefined}

          {onExtended ? (
            <>
              <Divider style={{ margin: "4px 0" }} />

              <div style={{ display: "flex", justifyContent: "center" }}>
                <Button type="text" icon={<AntIcon type="zoom" />} onClick={onExtended}>
                  {t("agent.filter.extendedFilter")}
                </Button>
              </div>
            </>
          ) : undefined}
        </>
      )}
    />
  );
};
